import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { take } from 'rxjs';

import { ConfigurationService } from '@sondermind/configuration';
import { Logger } from '@sondermind/datadog-logging';
import {
  getTranscendConsent,
  hasExplicitConsent,
  showConsentManager,
  setConsent,
  REQUIRED_CONSENT_KEYS,
} from '@sondermind/analytics';

@Injectable({
  providedIn: 'root'
})
export class ConsentSyncService {
  private isRestoringConsent = false;

  constructor(
    private http: HttpClient,
    private configurationService: ConfigurationService,
    private logger: Logger,
  ) { }

  registerConsentListener(): void {
    if (typeof window !== 'undefined') {
      (window as any)?.airgap?.ready?.((airgap: any) => {
        this.restoreConsent();
        airgap.addEventListener('consent-change', () => {
          if (this.isRestoringConsent) return;
          this.syncConsent();
        });
      });
    }
  }

  syncOrPrompt(): void {
    if (hasExplicitConsent()) {
      this.syncConsent();
    } else {
      showConsentManager()
        .catch(() => this.logger.warn('ConsentSyncService: showConsentManager failed'));
    }
  }

  private restoreConsent(): void {
    const url = `${this.configurationService.env.svcBase}/core/consent_preferences`;

    this.http.get<{ preferences: Record<string, boolean> | null }>(url).pipe(take(1)).subscribe({
      next: ({ preferences }) => {
        if (!preferences || !REQUIRED_CONSENT_KEYS.every((key) => key in preferences)) {
          this.syncOrPrompt();
          return;
        }
        this.isRestoringConsent = true;
        setConsent(preferences)
          .catch(() => this.logger.warn('ConsentSyncService: failed to apply restored consent preferences'))
          .finally(() => { this.isRestoringConsent = false });
      },
      error: (err: HttpErrorResponse) => {
        this.logger.warn(
          'ConsentSyncService: failed to restore consent preferences',
          { status: err.status },
        );
        this.syncOrPrompt();
      },
    });
  }

  syncConsent(): void {
    if (typeof window === 'undefined' || !window?.airgap?.getConsent) {
      return;
    }

    const preferences = getTranscendConsent();

    const hasAllCategories = preferences != null && REQUIRED_CONSENT_KEYS.every((key) => key in preferences);
    if (!hasAllCategories) {
      this.logger.debug('ConsentSyncService: skipping sync, consent categories not fully initialized', { preferences });
      return;
    }

    const url = `${this.configurationService.env.svcBase}/core/consent_preferences`;

    this.http.put(url, { preferences }).subscribe({
      error: (err) => this.logger.warn('ConsentSyncService: failed to sync consent preferences', { status: err.status }),
    });
  }
}
